import { cn } from "@kyan/ui";
import { forwardRef, type HTMLAttributes } from "react";

import { getPortfolioImpactState } from "#/utils/getPortfolioImpactState";
import { formatPercent } from "#/utils/valueFormatters";

export type MarginRatioType = "MMr" | "IMr";
export type MarginRatioSize = "md" | "lg";

export interface MarginRatioProps extends HTMLAttributes<HTMLDivElement> {
  size?: MarginRatioSize;
  type?: MarginRatioType;
  value: number;
  compact?: boolean;
  small?: boolean;
  precision?: number;
  valueClassName?: string;
}

const dangerGradient = "from-error from-55% dark:to-[#3C090D]";
const errorGradient = "from-orange-500 from-35% dark:to-orange-900";
const warningGradient = "from-warning from-15%";
const successGradient = "from-success from-15% dark:to-[#093E2B]";

const getImpactGradient = (value: number) => {
  const impact = getPortfolioImpactState(value);

  if (impact === "danger") return dangerGradient;
  if (impact === "error") return errorGradient;
  if (impact === "warning") return warningGradient;
  return successGradient;
};

export const MarginRatio = forwardRef<HTMLDivElement, MarginRatioProps>(
  (
    {
      size = "md",
      type = "MMr",
      value,
      compact = false,
      small = false,
      className,
      precision = 0,
      valueClassName,
      ...props
    },
    ref,
  ) => {
    const impactComponent = (
      <div
        className={cn(
          "h-1.25 rounded-[100px] bg-linear-to-r",
          {
            "w-4.5": size === "md",
            "w-12": size === "lg",
          },
          getImpactGradient(value),
        )}
      />
    );

    const percentageComponent = (
      <span
        className={cn(
          "text-textPrimary w-6.75",
          { "w-auto": small },
          valueClassName,
        )}
      >
        {formatPercent(value, {
          precision,
          signDisplay: "negative",
        })}
      </span>
    );

    if (small) {
      return (
        <div
          ref={ref}
          className={cn("flex items-center gap-1.5", className)}
          {...props}
        >
          {percentageComponent}
          {impactComponent}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-end text-end",
          {
            "gap-1 text-xs": size === "md",
            "gap-2.5": size === "lg",
            "flex-col items-end gap-1.5": compact,
          },
          className,
        )}
        {...props}
      >
        <span
          className={cn("text-textSecondary", {
            "w-6.5": size === "md",
            "w-7.5": size === "lg",
          })}
        >
          {type}
        </span>
        <div className="flex items-center">
          {impactComponent}
          {percentageComponent}
        </div>
      </div>
    );
  },
);

MarginRatio.displayName = "MarginRatio";
